'use client'

import type { ComponentProps, ReactNode } from 'react'

import Link from 'next/link'
import { CaretDownIcon, SettingsIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'

/*
 * GoodUnited / Sidebar. Figma: 10 / Components / Sidebar.
 * surface sidebar keeps GoodUnited's dark navigation (Colors/nav) in both modes; the active item sits on
 *   Colors/nav/item/active-background with Colors/nav/label/active and Colors/nav/icon/active.
 * surface surface follows the content theme; the active item sits on Colors/surface/subtle with
 *   Colors/text/brand and Colors/icon/brand, its chevron stays Colors/icon/secondary.
 * SidebarMenuButton: always 36px (never shrinks in a scrolling list), 6px corners, 24px icon,
 *   Roboto / text-base / medium, 16px chevron.
 * SidebarMenuSubButton: 40px inset with a 1px guide line, Roboto / text-base. The guide beside the active
 *   sub item is Colors/nav/guide/active (white) on the sidebar and Colors/border/selected on surface.
 */
type SidebarSurface = 'sidebar' | 'surface'

interface SidebarMenuButtonProps extends Omit<ComponentProps<'button'>, 'children'> {
    icon?: ReactNode
    label: ReactNode
    surface?: SidebarSurface
    active?: boolean
    expandable?: boolean
    expanded?: boolean
    href?: string
    trailing?: ReactNode
}

const buttonClasses = (surface: SidebarSurface, active: boolean) => cn(
    'group/menu-button flex h-9 w-full shrink-0 cursor-pointer items-center gap-2 rounded-md px-2 text-left text-base font-medium transition-colors outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-border-focus [&_svg]:shrink-0',
    surface === 'sidebar' && (active
        ? 'bg-nav-item-active text-nav-label-active'
        : 'text-nav-label hover:text-nav-label-hover'),
    surface === 'surface' && (active
        ? 'bg-surface-subtle text-text-brand'
        : 'text-text-primary hover:bg-surface-subtle')
)

const iconClasses = (surface: SidebarSurface, active: boolean) => cn(
    'inline-flex [&_svg]:size-6',
    surface === 'sidebar' && (active ? 'text-nav-icon-active' : 'text-nav-icon group-hover/menu-button:text-nav-icon-hover'),
    surface === 'surface' && (active ? 'text-icon-brand' : 'text-icon-secondary')
)

const chevronClasses = (surface: SidebarSurface, active: boolean) => cn(
    'size-4 transition-transform',
    surface === 'sidebar' && (active ? 'text-nav-icon-active' : 'text-nav-icon group-hover/menu-button:text-nav-icon-hover'),
    surface === 'surface' && 'text-icon-secondary'
)

function SidebarMenuButton({ icon, label, surface = 'sidebar', active = false, expandable = false, expanded = false, href, trailing, className, ...props }: SidebarMenuButtonProps) {
    const content = (
        <>
            {icon && (
                <span className={iconClasses(surface, active)} aria-hidden='true'>
                    {icon}
                </span>
            )}
            <span className='min-w-0 flex-1 truncate'>
                {label}
            </span>
            {trailing}
            {expandable && (
                <CaretDownIcon className={cn(chevronClasses(surface, active), expanded && 'rotate-180')} aria-hidden='true' />
            )}
        </>
    )

    if (href) {
        return (
            <Link
                href={href}
                data-slot='sidebar-menu-button'
                data-active={active || undefined}
                aria-current={active ? 'page' : undefined}
                className={cn(buttonClasses(surface, active), className)}
            >
                {content}
            </Link>
        )
    }

    return (
        <button
            type='button'
            data-slot='sidebar-menu-button'
            data-active={active || undefined}
            aria-expanded={expandable ? expanded : undefined}
            className={cn(buttonClasses(surface, active), className)}
            {...props}
        >
            {content}
        </button>
    )
}

interface SidebarMenuSubButtonProps {
    label: ReactNode
    surface?: SidebarSurface
    active?: boolean
    href?: string
    badge?: ReactNode
    className?: string
    onClick?: () => void
}

function SidebarMenuSubButton({ label, surface = 'sidebar', active = false, href, badge, className, onClick }: SidebarMenuSubButtonProps) {
    const classes = cn(
        'relative flex h-9 w-full shrink-0 cursor-pointer items-center gap-1.5 rounded-md pr-2 pl-10 text-left text-base transition-colors outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-border-focus',
        'before:absolute before:inset-y-0 before:left-5 before:w-px',
        surface === 'sidebar' && (active
            ? 'text-nav-label-active before:bg-nav-guide-active'
            : 'text-nav-label before:bg-nav-divider hover:text-nav-label-hover'),
        surface === 'surface' && (active
            ? 'font-medium text-text-brand before:bg-border-selected'
            : 'text-text-secondary before:bg-border-subtle hover:text-text-primary'),
        className
    )

    const content = (
        <>
            <span className='min-w-0 truncate'>
                {label}
            </span>
            {badge}
        </>
    )

    if (href) {
        return (
            <Link href={href} data-slot='sidebar-menu-sub-button' aria-current={active ? 'page' : undefined} className={classes}>
                {content}
            </Link>
        )
    }

    return (
        <button type='button' data-slot='sidebar-menu-sub-button' onClick={onClick} className={classes}>
            {content}
        </button>
    )
}

interface SidebarMenuActionProps extends Omit<ComponentProps<'button'>, 'children'> {
    surface?: SidebarSurface
    direction?: 'down' | 'up'
    active?: boolean
}

/* SidebarMenuAction: a 24px chevron control that opens or closes a group. */
function SidebarMenuAction({ surface = 'sidebar', direction = 'down', active = false, className, ...props }: SidebarMenuActionProps) {
    return (
        <button
            type='button'
            data-slot='sidebar-menu-action'
            aria-label={direction === 'down' ? 'Expand' : 'Collapse'}
            className={cn(
                'inline-flex size-6 cursor-pointer items-center justify-center rounded-sm transition-colors outline-none focus-visible:outline-2 focus-visible:outline-border-focus [&_svg]:size-4',
                surface === 'sidebar' ? (active ? 'text-nav-icon-hover' : 'text-nav-icon hover:text-nav-icon-hover') : 'text-icon-secondary hover:text-icon-hover',
                className
            )}
            {...props}
        >
            <CaretDownIcon className={cn(direction === 'up' && 'rotate-180')} aria-hidden='true' />
        </button>
    )
}

/* SidebarMenuAction/Settings: the 28px settings control in the sidebar footer. */
function SidebarMenuActionSettings({ className, ...props }: Omit<ComponentProps<'button'>, 'children'>) {
    return (
        <button
            type='button'
            data-slot='sidebar-menu-action-settings'
            aria-label='Settings'
            className={cn('inline-flex size-7 cursor-pointer items-center justify-center rounded-md text-nav-icon transition-colors outline-none hover:text-nav-icon-hover focus-visible:outline-2 focus-visible:outline-border-focus [&_svg]:size-5', className)}
            {...props}
        >
            <SettingsIcon aria-hidden='true' />
        </button>
    )
}

export {
    SidebarMenuAction,
    SidebarMenuActionSettings,
    SidebarMenuButton,
    SidebarMenuSubButton
}
export type { SidebarSurface }
